import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Box,
  IconButton,
  Card,
  CardContent,
  Divider,
  Chip,
  Alert,
} from "@mui/material";
import AcceptQuatationSubmit from "@/app/features/tender/components/view/quatation-accept-sbmit";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { X, Package, Calendar, Truck, CreditCard, Info } from "lucide-react";
import TableComponent from "@/app/global-components/table";
import RefuseQuatationSubmit from "@/app/features/tender/components/view/quatation-refuse-sbmit";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ROLE } from "@/types";
// import { cn } from "@/lib/utils";
import { isNull } from "lodash";
import useOffer from "@/hooks/useTender";
import { showToast } from "@/app/global-components/toaster";
import { routes } from "@/consts";
import { useNavigate } from "react-router-dom";
import useOrder from "@/hooks/use-order";
import { Checkbox } from "@/app/global-components/checkbox";
import { Button } from "@/app/global-components/button";
import { toggleSelectedItem, toggleAllItems } from "@/store/authSlice";

export interface QuotationType {
  id: string;
  products: {
    quatationId: string;
    productId: string;
    unitName: string;
    DeliveryMethod: string;
    quantity: number;
    unitPrice: number;
    vat: number;
    notice: string;
    attachment?: File | null;
    images?: string[] | null | undefined;
  }[];
  paymentMethod: string;
  DeadLineDate: string;
  DeliveryMethod: string;
  contactMethod: string;
  status: string;
  tenderId: string;
  companyId: string;
  company: {
    id: string;
    nameAr: string;
    CompanyNr: string;
    nameEn: string;
    website: string;
    address: string;
  };
  userId: string;
  deliverDays: number;
  OpportunityNr: number;
  tender: {
    id: string;
    title: string;
    value: number;
    endDate: string;
    deliverDate: string;
    type: string;
    status: string;
    categoriesIds: string[];
    categories: string[];
    region: string;
    city: string;
    fileName: string;
    fileDescription: string;
    fileId: string | null;
    attachmentName: string;
    attachmentDescription: string;
    attachmentId: string | null;
    attachmentDeliverDays: string | null;
    receiveDocumentsType: string;
    Paylater: string;
    companyId: string;
    userId: string;
    products: [
      {
        item: string;
        quantity: number;
        unit: string;
        _id: string;
      }
    ];
    TenderNr: number;
  };
}

interface QuotationDetailsModalProps {
  open: boolean;
  onClose: () => void;
  quotation: QuotationType | null;
  offerStatus?: string;
}

const QuotationDetailsModal: React.FC<QuotationDetailsModalProps> = ({
  open,
  onClose,
  quotation,
  offerStatus,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  // const theme = useTheme();

  if (!quotation) return null;

  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = authUser?.role;
  const { acceptQuatation, refuseQuatation, isLoading, error } = useOffer();
  const navigate = useNavigate();
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const { createOrder } = useOrder();
  const quatation = useSelector((state: RootState) => state.auth.quatation);
  const selectedItems = quatation?.selectedItems || [];
  const dispatch = useDispatch();
  const totalVat = quotation.products.reduce(
    (acc: any, product: any) =>
      acc +
      (product.unitPrice ?? 0) *
        (product.quantity ?? 0) *
        ((product.vat ?? 0) / 100),
    0
  );

  const proccessOrder = async () => {
    if (userRole === ROLE.BUYER) {
      const orderData = {
        title: "Order for Tender",
        deliverDate: new Date(
          new Date().getTime() + 7 * 24 * 60 * 60 * 1000
        ).toISOString(),
        type: "Tiny",
        status: "Planning",
        region: myCompany.region,
        city: myCompany.city,
        address: myCompany.address,
        products: quotation.products?.map((item) => ({
          itemId: item.productId,
          item: item.unitName,
          quantity: item.quantity,
          deliveryMethod: quotation.DeliveryMethod,
          discount: 0,
          price: item.unitPrice,
          vat: item.vat || 15,
          notice: item.notice || "",
          image: item.images ? item.images[0] : "",
          companyId: quotation.companyId,
          tender: quotation.tenderId,
          quatationId: quotation.id,
        })),
        tenderId: quotation.tenderId,
        userId: authUser?.id,
        companyId: myCompany?.id,
        Sendedproducts: [],
        OrderNr: myCompany ? myCompany.orderNr + 1 : 1,
      };
      try {
        setIsPlacingOrder(true);
        const response = await createOrder(orderData);
        if (response?.isSuccess) {
          showToast(t("Alert.OrderCreated"), "success");
          handleAction(acceptQuatation, quotation.id);
          navigate(`${routes.order}/${response.data.id}`);
        } else {
          showToast(t("Alert.OrderCreationError"), "warning");
        }
      } catch (error) {
        showToast(t("Alert.OrderTryAgain"), "warning");
        console.error("Error creating order:", error);
      } finally {
        setIsPlacingOrder(false);
      }
    }
  };

  const getDateLabel = (days: string) => {
    switch (days) {
      case "7":
        return `7 ${t("time.days")}`;
      case "14":
        return `14 ${t("time.days")}`;
      case "30":
        return `30 ${t("time.days")}`;
      default:
        return `7 ${t("time.days")}`;
    }
  };

  const handleAction = async (action: Function, id: string | undefined) => {
    if (quotation && id) {
      try {
        await action(id);
        onClose();
      } catch (err) {
        console.error("Error performing action:", err);
      }
    }
  };

  const renderButton = () => {
    switch (
      quotation.products.filter((item) =>
        selectedItems.includes(item.productId)
      ).length
    ) {
      case quotation.products.length:
        return (
          userRole === ROLE.BUYER && (
            <AcceptQuatationSubmit
              isLoading={isPlacingOrder}
              onClick={proccessOrder}
            />
          )
        );
      default:
        return (
          userRole === ROLE.BUYER &&
          offerStatus != "Opened" && (
            <Button
              onClick={onClose}
              disabled={selectedItems.length === 0}
              className="bg-black text-white hover:bg-gray-800"
            >
              {t("actions.select")}
            </Button>
          )
        );
    }
  };

  const totalPrice = useMemo(
    () =>
      quotation.products.reduce((acc: any, product: any) => {
        if (selectedItems.length == 0) {
          return (
            acc + product.unitPrice * product.quantity * (1 + product.vat / 100)
          );
        }
        if (selectedItems.includes(product.productId)) {
          return (
            acc + product.unitPrice * product.quantity * (1 + product.vat / 100)
          );
        } else {
          return acc;
        }
        // return acc + product.unitPrice * product.quantity;
      }, 0),
    [selectedItems]
  );

  const columns = [
    {
      title: userRole === ROLE.BUYER &&
        offerStatus != "Opened" &&
        offerStatus != "Accepted" && (
          <Checkbox
            className="w-6 h-6 border-2 border-zinc-400"
            checked={selectedItems.length === quotation.products.length}
            onCheckedChange={() =>
              dispatch(
                toggleAllItems(
                  selectedItems.length === quotation.products.length
                    ? []
                    : quotation.products.map((item) => item.productId)
                )
              )
            }
          />
        ),
      field: "checkbox",
      render: (row: any) =>
        userRole === ROLE.BUYER &&
        offerStatus != "Opened" &&
        offerStatus != "Accepted" && (
          <Checkbox
            className="w-6 h-6 border-2 border-zinc-400"
            checked={selectedItems.some((item) => item === row.productId)}
            onCheckedChange={() => dispatch(toggleSelectedItem(row.productId))}
          />
        ),
    },
    {
      title: t("item.fields.image"),
      field: "image",
      render: (row: any) =>
        row.images ? (
          <img
            src={row.images[0]}
            alt="item"
            className="w-16 h-16 rounded-lg object-cover border border-gray-200"
          />
        ) : (
          <Box className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
            <Package size={20} className="text-gray-400" />
          </Box>
        ),
    },
    {
      title: t("checkout.fields.name"),
      field: "unitName",
      render: (row: any) => (
        <Typography variant="body2" className="font-medium">
          {row.unitName}
        </Typography>
      ),
    },
    {
      title: t("checkout.fields.quantity"),
      field: "quantity",
      render: (row: any) => (
        <Chip
          label={row.quantity}
          size="small"
          variant="outlined"
          className="font-medium"
        />
      ),
    },
    {
      title: t("checkout.fields.unitPrice"),
      field: "unitPrice",
      render: (row: any) => (
        <Typography variant="body2" className="font-semibold text-gray-900">
          {row.unitPrice} {t("currency")}
        </Typography>
      ),
    },
    {
      title: t("checkout.fields.vat"),
      field: "vat",
      render: (row: any) => (
        <Typography variant="body2" className="font-semibold text-gray-900">
          {row.vat || "0"} %
        </Typography>
      ),
    },
    {
      title: t("orders.fields.notes"),
      field: "notice",
      render: (row: any) => (
        <Typography variant="body2" className="text-gray-600">
          {row.notice || t("orders.fields.noNotes")}
        </Typography>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
          m: 2,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
          borderBottom: "1px solid #e5e7eb",
          p: 3,
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#374151" }}>
            {t("quotations.details")}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#6b7280",
              "&:hover": {
                backgroundColor: "#f3f4f6",
                color: "#374151",
              },
            }}
          >
            <X size={24} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Company Information */}
        <Card
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: "none",
            border: "1px solid #e5e7eb",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, color: "#374151" }}
            >
              {t("checkout.fields.company")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  {t("general.number")}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {quotation.company.CompanyNr}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Products Table */}
        <Typography
          variant="h6"
          sx={{ mb: 2, fontWeight: 600, color: "#374151" }}
        >
          {t("products.list")}
        </Typography>

        <TableComponent
          columns={columns}
          data={quotation.products}
          rowKeyField="productId"
          maxHeight={300}
          pagination={false}
          variant="bordered"
          size="sm"
        />

        {/* Quotation Details */}
        <Card
          sx={{
            mt: 3,
            borderRadius: 2,
            boxShadow: "none",
            border: "1px solid #e5e7eb",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 3, fontWeight: 600, color: "#374151" }}
            >
              {t("general.information")}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Calendar size={20} className="text-gray-500 mr-2" />
                  <Typography variant="body2" color="textSecondary">
                    {t("delivery.suggestedTime")}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {isNull(quotation.deliverDays)
                    ? t("general.noDataMatchingCriteria")
                    : `${quotation.deliverDays} ${t("time.days")}`}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Info size={20} className="text-gray-500 mr-2" />
                  <Typography variant="body2" color="textSecondary">
                    {t("offers.validityPeriod1")}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {isNull(quotation.DeadLineDate)
                    ? t("general.noDataMatchingCriteria")
                    : getDateLabel(quotation.DeadLineDate)}
                </Typography>
              </Grid>

              {userRole !== ROLE.BUYER && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <CreditCard size={20} className="text-gray-500 mr-2" />
                    <Typography variant="body2" color="textSecondary">
                      {t("payment.method1")}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {isNull(quotation.paymentMethod)
                      ? t("general.noDataMatchingCriteria")
                      : quotation.paymentMethod === "1"
                      ? t("payment.direct")
                      : t("payment.deferred1")}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Truck size={20} className="text-gray-500 mr-2" />
                  <Typography variant="body2" color="textSecondary">
                    {t("delivery.title")}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {isNull(quotation.DeliveryMethod)
                    ? t("general.noDataMatchingCriteria")
                    : quotation.DeliveryMethod}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mt: 3, borderRadius: 2, border: "1px solid #e5e7eb" }}>
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 3, fontWeight: 600, color: "#374151" }}
            >
              {t("general.summary")}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body1" color="textSecondary">
                  {t("item.fields.vat")}:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {totalVat.toFixed(2)} {t("currency")}
                </Typography>
              </Box>

              <Divider />

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  {t("orders.fields.totalPrice")}:
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#644980" }}
                >
                  {totalPrice.toFixed(2)} {t("currency")}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </DialogContent>

      {quotation.status != "Canceled" && (
        <DialogActions
          sx={{
            p: 3,
            background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
            borderTop: "1px solid #e5e7eb",
            gap: 2,
          }}
        >
          {userRole === ROLE.BUYER &&
            offerStatus !== "Accepted" &&
            offerStatus != "Opened" && (
              <RefuseQuatationSubmit
                isLoading={isLoading}
                onClick={() => handleAction(refuseQuatation, quotation.id)}
              />
            )}

          {offerStatus !== "Accepted" && renderButton()}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default QuotationDetailsModal;
